import { bufferToHexString } from '@iotize/common/byte-converter';
import { Decoder, Encoder } from '@iotize/common/converter/api';
import {
  ExecutionContext,
  ProtocolChangeEvent,
  RequestInterceptorType,
  TapClientInterface,
  TapRequestFrame,
  TapResponseFrame,
} from '@iotize/tap/client/api';
import {
  ComProtocol,
  ConnectionStateChangeEvent,
} from '@iotize/tap/protocol/api';
import { Observable, Subject, throwError } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  TAP_REQUEST_FRAME_CONVERTER,
  TAP_RESPONSE_FRAME_CONVERTER,
} from './converters';
import { debug } from './debug';
import { InterceptorChain } from './interceptors/interceptor-chain';
import { TapClientError } from './tap-client-error';
import { TapRequestHelper } from './request/tap-request-helper';

const TAG = 'Client';

export class TapClient implements TapClientInterface {
  private _commandEncoder: Encoder<TapRequestFrame, Uint8Array>;
  private _responseDecoder: Decoder<Uint8Array, TapResponseFrame>;
  private _onProtocolChange = new Subject<ProtocolChangeEvent>();
  private protocols: { [type: string]: ComProtocol };
  private currentProtocolId?: string;
  private _interceptorChain: InterceptorChain;

  public static create(protocol?: ComProtocol) {
    const client = new TapClient(
      TAP_REQUEST_FRAME_CONVERTER,
      TAP_RESPONSE_FRAME_CONVERTER,
    );
    if (protocol) {
      client.addComProtocol(protocol);
    }
    return client;
  }

  constructor(
    requestEncoder: Encoder<TapRequestFrame, Uint8Array>,
    responseDecoder: Decoder<Uint8Array, TapResponseFrame>,
  ) {
    this._commandEncoder = requestEncoder;
    this._responseDecoder = responseDecoder;
    this.protocols = {};
    this._interceptorChain = new InterceptorChain(
      (context: ExecutionContext) => {
        try {
          const command = context.request;
          // const bodyDecoder = context.bodyDecoder;
          return this._command<any>(command);
        } catch (err) {
          return throwError(err);
        }
      },
    );
  }

  // public isEncryptionEnabled(): boolean {
  //     return this._options.encryption;
  // }

  // public get cryptedFrameConverter() {
  //     return this._cryptedFrameConverter;
  // }

  public get interceptorChain(): InterceptorChain {
    return this._interceptorChain;
  }

  public addInterceptor(interceptor: RequestInterceptorType): this {
    this.interceptorChain.addInterceptor(interceptor);
    return this;
  }

  // /**
  //  * Enable/Disable encrytion when communicating with a device
  //  * @param value true if requests must be encrypted
  //  * @param algo optional if you have already provided the algo with @{link setEncryptionAlgo(algo)} method
  //  */
  // public enableEncryption(value: boolean, algo?: EncryptionAlgo): Promise<any> {
  //     if (value) {
  //         algo = algo || this._encryptionAlgo;
  //         if (!algo) {
  //             return Promise.reject(
  //                 TapClientError.illegalArgument(
  //                     'Illegal argument, encryption algo must be set'
  //                 )
  //             );
  //         }
  //         return this._startEncryption(algo);
  //     }
  //     else {
  //         return this._stopEncryption();
  //     }
  // }

  public isConnected(): boolean {
    return (
      this.hasProtocol(this.currentProtocolId) &&
      this.getCurrentProtocol().isConnected()
    );
  }

  /**
   * see {@link getCurrentProtocol}
   */
  get protocol(): ComProtocol {
    return this.getCurrentProtocol();
  }

  get commandEncoder(): Encoder<TapRequestFrame, Uint8Array> {
    return this._commandEncoder;
  }

  get responseDecoder() {
    return this._responseDecoder;
  }

  /**
   * Get current protocol or throw an error if no protocol
   */
  public getCurrentProtocol(): ComProtocol {
    if (!this.currentProtocolId) {
      throw TapClientError.illegalStateError('Tap client protocol is required');
    }
    if (!this.hasProtocol(this.currentProtocolId)) {
      throw TapClientError.illegalArgument(
        'Protocol ' + this.currentProtocolId + ' does not exist',
      );
    }
    return this.protocols[this.currentProtocolId];
  }

  /**
   * Register/replace a communication protocol with given id
   * If no communication protocol is selected yet, it will select it.
   * @param newProtocol
   * @param id
   */
  public addComProtocol(newProtocol: ComProtocol, id = 'default'): this {
    this.protocols[id] = newProtocol;
    if (!this.currentProtocolId) {
      this.currentProtocolId = id;
    }
    return this;
  }

  /**
   * Switch to given communication protocol identifier
   * @throws if protocol identifier does not exist
   * @param name
   */
  public switchProtocol(name: string): this {
    if (!this.hasProtocol(name)) {
      throw TapClientError.illegalArgument('Unkonwn protocol: ' + name);
    }
    const oldProtocol = this.getCurrentProtocol();
    this.currentProtocolId = name;
    debug(TAG, `Changing protocol to: ${name}`);
    this._onProtocolChange.next({
      newProtocol: this.getCurrentProtocol(),
      oldProtocol,
    });
    return this;
  }

  /**
   * Change communication protocol
   * @throws if protocol identifier does not exist
   * @param protocol can either be the communication protocol instance or a string identifier
   */
  public useComProtocol(protocol: ComProtocol | string): this {
    let protocolId;
    if (typeof protocol == 'string') {
      protocolId = protocol;
    } else {
      protocolId = protocol.constructor.name;
      this.addComProtocol(protocol, protocolId);
    }
    this.switchProtocol(protocolId);
    return this;
  }

  /**
   * Return true if protocol identifier is registered
   * @param id
   */
  public hasProtocol(id: any): boolean {
    return id in this.protocols;
  }

  public connect(): Observable<any> {
    try {
      return this.getCurrentProtocol().connect();
    } catch (err) {
      return throwError(err);
    }
  }

  public disconnect(): Observable<any> {
    try {
      return this.getCurrentProtocol().disconnect();
    } catch (err) {
      return throwError(err);
    }
  }

  /**
   * Send request
   * @param request
   * @param bodyDecoder
   */
  public request<T>(request: TapRequestFrame): Observable<TapResponseFrame> {
    return this._interceptorChain.execute<T>({
      // bodyDecoder,
      request,
      client: this,
    });
  }

  /**
   * Send raw request
   * No interceptors/No encryption will be triggered
   * @param dataIn request bytes
   * @param bodyDecoder optional body decoder to use
   */
  public send<T>(dataIn: Uint8Array): Observable<TapResponseFrame> {
    try {
      return this.getCurrentProtocol()
        .send(dataIn)
        .pipe(
          map((result: Uint8Array) => {
            // debug(
            //     TAG,
            //     'Received result: ',
            //     bufferToHexString(result),
            //     'DECODER: ',
            //     bodyDecoder ? bodyDecoder.constructor.name : 'NO'
            // );
            try {
              return this._responseDecoder.decode(result);
            } catch (err) {
              throw TapClientError.decodeResponseError(err as Error, dataIn);
            }
          }),
        );
    } catch (err) {
      return throwError(err);
    }
  }

  /**
   * Listen to connection state change event
   * We have to resubscribe when protocol change..
   */
  public onConnectionStateChange(): Observable<ConnectionStateChangeEvent> {
    try {
      return this.getCurrentProtocol().onConnectionStateChange();
    } catch (err) {
      return throwError(err);
    }
  }

  public onProtocolChange(): Observable<ProtocolChangeEvent> {
    try {
      return this._onProtocolChange.asObservable();
    } catch (err) {
      return throwError(err);
    }
  }

  protected _command<T>(
    command: TapRequestFrame,
    // bodyDecoder?: BodyDecoder<T>
  ): Observable<TapResponseFrame> {
    try {
      if (!this.isConnected()) {
        return throwError(TapClientError.notConnectedError());
      }
      let dataIn: Uint8Array;
      try {
        dataIn = this._commandEncoder.encode(command);
      } catch (e) {
        return throwError(
          TapClientError.encodeRequestError(command, e as Error),
        );
      }

      debug(
        TAG,
        `Sending ${TapRequestHelper.toString(command)} (${
          dataIn.length
        } bytes 0x${bufferToHexString(dataIn)})`,
      );

      return this.getCurrentProtocol()
        .send(dataIn)
        .pipe(
          map((result: Uint8Array) => {
            // debug(
            //     TAG,
            //     'Received result: ',
            //     bufferToHexString(result),
            //     'DECODER: ',
            //     bodyDecoder ? bodyDecoder.constructor.name : 'NO'
            // );
            try {
              const response = this._responseDecoder.decode(result);
              // if (bodyDecoder) {
              //     response.setBodyDecoder(bodyDecoder);
              // }
              return response;
            } catch (err) {
              throw TapClientError.decodeResponseError(err as Error, command);
            }
          }),
          // map(response => {
          //     debug(
          //         TAG,
          //         `Command "${command.toString()}" => Response: ${response.toString()}`
          //     );
          //     if (response instanceof ResponseImpl) {
          //         (response as ResponseImpl<T>).setRequest(command);
          //     }
          //     return response;
          // })
        );
    } catch (err) {
      return throwError(err);
    }
  }
}
